import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { showSuccess } from '../../0_presentation/core/components/feedback/snackbar';
import {
  StaffDeleteDialog,
  StaffEditDialog,
} from '../../0_presentation/setting/components/staffDialogs';
import IStaffRepository from '../../2_domain/staff/iStaffRepository';
import { Staff } from '../../2_domain/staff/staff';
import Get from '../../lib/di/get';
import promiseResolver from '../../lib/util/promiseResolver';
import useDialog from '../core/components/feedback/useDialog';

const emptyPagination: Pagination = {
  isAscending: false,
  pageNumber: 0,
  pageSize: 20,
};

const emptyPageResult: Page = {
  totalElements: 0,
  pageNumber: 0,
};
const useStaffListPageController = () => {
  const staffRepo = Get.find<IStaffRepository>('IStaffRepository');

  const [staffKind, setStaffKind] = useState<StaffKind | 'All'>('All');
  const [searchText, setSearchText] = useState('');
  const [pageResult, setPageResult] = useState(emptyPageResult);
  const [pagination, setPagination] = useState(emptyPagination);
  const [staffs, setStaffs] = useState<Staff[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const dialog = useDialog();
  const navigate = useNavigate();
  const theme = useTheme();

  const onRefresh = () => {
    setSearchText('');
    setStaffKind('All');
  };

  const onSearch = async () => {
    promiseResolver({
      promise: staffRepo.getStaffs(staffKind, searchText, pagination),
      successCallback: (ret) => {
        setStaffs(ret.list);
        setPageResult(ret.page ?? { pageNumber: '0', totalElements: '0' });
      },
      isLoadingCallback: setIsLoading,
    });
  };

  useEffect(() => {
    onSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination]);

  const setPageNumber = (newPageNumber: number) => {
    setPagination((prev) => {
      return { ...prev, pageNumber: newPageNumber };
    });
  };

  const setPageSize = (newPageSize: number) => {
    setPagination((prev) => {
      return { ...prev, pageNumber: newPageSize };
    });
  };

  const setSortFilter = (sortFiler: SortFilter) => {
    setPagination((prev) => {
      return { ...prev, isAscending: sortFiler === 'Newest' };
    });
  };

  const onStaffManage = (manageType: 'Edit' | 'Delete', staff: Staff) => {
    if (manageType === 'Edit') {
      dialog.openDialog(
        <StaffEditDialog
          staff={staff}
          onSave={editStaff}
          onCancel={async () => dialog.closeDialog()}
        ></StaffEditDialog>
      );
    } else if (manageType === 'Delete') {
      dialog.openDialog(
        <StaffDeleteDialog
          staff={staff}
          onCancel={async () => dialog.closeDialog()}
          onDelete={async () => deleteStaff(staff)}
        ></StaffDeleteDialog>
      );
    } else {
      throw new Error('invalid manage Type');
    }
  };

  const onNewStaffHandler = async () => {
    dialog.openDialog(
      <StaffEditDialog
        onSave={addStaff}
        onCancel={async () => dialog.closeDialog()}
      ></StaffEditDialog>
    );
  };

  const editStaff = async (staff: Staff) => {
    await promiseResolver({
      promise: staffRepo.updateStaff(staff),
      successCallback: () => {
        showSuccess(theme.texts.updateSuccess);
        dialog.closeDialog();
        setStaffs((prev) => {
          const newStaff = [...prev];
          return newStaff.map((s) => {
            if (s.id === staff.id) {
              return staff;
            } else {
              return s;
            }
          });
        });
      },
    });
  };

  const addStaff = async (staff: Staff) => {
    await promiseResolver({
      promise: staffRepo.createStaff(staff),
      successCallback: () => {
        showSuccess(theme.texts.createSuccess);
        dialog.closeDialog();
        navigate(0);
      },
    });
  };

  const deleteStaff = async (staff: Staff) => {
    await promiseResolver({
      promise: staffRepo.deleteStaff(staff.id),
      successCallback: () => {
        showSuccess(theme.texts.deleteSuccess);
        dialog.closeDialog();
        navigate(0);
      },
    });
  };

  return {
    pagination,
    pageResult,
    staffKind,
    setStaffKind,
    searchText,
    setSearchText,
    onSearch,
    onRefresh,
    staffs,
    onStaffManage,
    setPageSize,
    setPageNumber,
    setSortFilter,
    onNewStaffHandler,
    isLoading,
  };
};

export default useStaffListPageController;
